import React, { useRef, useState } from "react";

const actionLabels = {
  validate_zip: "ZIP 사전 검증",
  check: "검사 실행",
  upload_zip: "ZIP 적용",
  frontend_build: "프론트 빌드",
  restart_backend: "백엔드 재시작",
  safe_run: "안전 배포",
};

function formatDeployTime(value) {
  if (!value) return "";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function DeploymentPanel({
  onValidateZip,
  onUpload,
  onCheck,
  onBuild,
  onRestart,
  onSafeDeploy,
  onActionComplete,
  deployRuns = [],
}) {
  const [busyAction, setBusyAction] = useState("");
  const [result, setResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validation, setValidation] = useState(null);
  const fileInputRef = useRef(null);

  const runAction = async (action, runner) => {
    setBusyAction(action);
    setResult(null);
    try {
      const data = await runner();
      setResult(data);
      await onActionComplete?.();
      return data;
    } catch (err) {
      const failed = { status: "failed", ok: false, log: err.message };
      setResult(failed);
      await onActionComplete?.();
      return failed;
    } finally {
      setBusyAction("");
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setValidation(null);
    setResult(null);
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      setResult({ status: "failed", ok: false, log: "먼저 배포 ZIP 파일을 선택해주세요." });
      return;
    }
    const data = await runAction("validate", () => onValidateZip(selectedFile));
    if (data?.ok) setValidation(data);
  };

  const handleUpload = () => {
    if (!selectedFile) {
      setResult({ status: "failed", ok: false, log: "먼저 배포 ZIP 파일을 선택해주세요." });
      return;
    }
    if (!validation?.ok || !validation?.validation_id) {
      setResult({ status: "failed", ok: false, log: "ZIP 사전 검증을 먼저 통과해야 적용할 수 있습니다." });
      return;
    }
    runAction("upload", () => onUpload(selectedFile, validation.validation_id));
  };

  const validationOk = Boolean(validation?.ok && validation?.validation_id);

  return (
    <section className="deployment-panel">
      <div className="deployment-header">
        <div>
          <p className="panel-kicker">배포 관리</p>
          <h3>검증 기반 ZIP 배포</h3>
          <p className="dashboard-muted">
            ZIP을 바로 적용하지 않고 사전 검증을 통과한 동일 파일만 적용합니다.
          </p>
        </div>
      </div>

      <div className="deploy-upload-box">
        <div>
          <strong>1. 배포 ZIP 선택</strong>
          <p className="dashboard-muted">
            허용된 TOBE AI 파일만 검증하고 Python 문법 오류와 ZIP 구조 오류를 먼저 차단합니다.
          </p>
          {selectedFile && (
            <p className="deploy-file-name">
              선택됨: {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
            </p>
          )}
          {validationOk && (
            <p className="deploy-file-name">
              검증 통과: {validation.validation_id} / 적용 예정 {validation.allowed?.length || 0}개
            </p>
          )}
        </div>
        <div className="deployment-steps">
          <button className="admin-approve-button" disabled={!!busyAction} onClick={() => fileInputRef.current?.click()}>
            ZIP 선택
          </button>
          <button className="admin-approve-button" disabled={!!busyAction || !selectedFile} onClick={handleValidate}>
            {busyAction === "validate" ? "검증 중..." : "2. 사전 검증"}
          </button>
          <button className="admin-danger-button" disabled={!!busyAction || !validationOk} onClick={handleUpload}>
            {busyAction === "upload" ? "적용 중..." : "3. 검증 ZIP 적용"}
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip,application/zip"
          className="hidden-file-input"
          onChange={handleFileChange}
        />
      </div>

      <div className="deployment-steps">
        <button className="admin-approve-button" disabled={!!busyAction} onClick={() => runAction("check", onCheck)}>
          {busyAction === "check" ? "검사 중..." : "4. 운영 검사"}
        </button>
        <button className="admin-approve-button" disabled={!!busyAction} onClick={() => runAction("build", onBuild)}>
          {busyAction === "build" ? "빌드 중..." : "5. 프론트 빌드"}
        </button>
        <button className="admin-danger-button" disabled={!!busyAction} onClick={() => runAction("restart", onRestart)}>
          {busyAction === "restart" ? "요청 중..." : "6. 백엔드 재시작"}
        </button>
        <button className="admin-danger-button" disabled={!!busyAction} onClick={() => runAction("safe", onSafeDeploy)}>
          {busyAction === "safe" ? "배포 중..." : "안전 배포"}
        </button>
      </div>

      <div className="deployment-note">
        <strong>권장 순서</strong>
        <span>ZIP 선택 → 사전 검증 → 검증 ZIP 적용 → 운영 검사 → 프론트 빌드 → 백엔드 재시작</span>
        <span>검증 후 파일을 다시 선택하면 검증 상태가 초기화됩니다.</span>
      </div>

      {result && (
        <div className={`deployment-result ${result.ok === false ? "failed" : "ok"}`}>
          <strong>상태: {result.status || (result.ok ? "성공" : "실패")}</strong>
          {result.message && <p>{result.message}</p>}
          {result.log && <pre className="patch-preview validation-log">{result.log}</pre>}
        </div>
      )}

      <div className="deployment-history">
        <div className="deployment-history-header">
          <strong>최근 배포 이력</strong>
          <span>검증, ZIP 적용, 빌드, 재시작 결과가 자동으로 쌓입니다.</span>
        </div>
        {deployRuns.length === 0 ? (
          <p className="dashboard-muted">아직 배포 이력이 없습니다.</p>
        ) : (
          <div className="deploy-history-list">
            {deployRuns.map((run) => (
              <details key={run.id} className={`deploy-history-item ${run.ok ? "ok" : "failed"}`}>
                <summary>
                  <span className="deploy-history-action">{actionLabels[run.action] || run.action}</span>
                  <span className="deploy-history-status">{run.status}</span>
                  <span className="deploy-history-time">{formatDeployTime(run.created_at)}</span>
                </summary>
                <div className="deploy-history-body">
                  <p>{run.message || "상세 메시지가 없습니다."}</p>
                  <p className="dashboard-muted">
                    실행자: {run.actor_userid || "-"} · 회사: {run.actor_company || "-"}
                  </p>
                  {run.log && <pre className="patch-preview validation-log">{run.log}</pre>}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default DeploymentPanel;
